"use client";

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";

import { performPasskeyAuthentication } from "@/lib/auth/webauthn-client";
import { customerDestination } from "@/lib/hosting/navigation";
import { isPlatformAdminRole } from "@/lib/auth/roles";
import type { Permission } from "./permissions";
import { isPermission } from "./permissions";
import type { PackageCapabilities } from "@/lib/packages/types";

export interface AuthMembership {
  accountId: string;
  accountName: string;
  accountSlug: string;
  accountStatus?: string;
  role: string;
  permissions?: string;
}

export interface AuthUser {
  id: string;
  username: string;
  email: string | null;
  role: string;
  displayName: string | null;
  timezone: string;
  lastLoginAt: string | null;
  createdAt: string;
  memberships?: AuthMembership[];
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isFirstRun: boolean;
  activeAccountId: string | null;
  capabilities: PackageCapabilities | null;
  permissions: Permission[];
  login: (
    username: string,
    password: string,
    options?: { totpCode?: string; recoveryCode?: string }
  ) => Promise<{ success: boolean; requiresPasskey?: boolean; requires2FA?: boolean; message?: string; error?: string }>;
  loginWithPasskey: (username?: string) => Promise<{ success: boolean; error?: string }>;
  setupAdmin: (username: string, password: string, displayName?: string) => Promise<{ success: boolean; error?: string }>;
  updateProfile: (data: { displayName?: string; email?: string; timezone?: string }) => Promise<{ success: boolean; error?: string }>;
  switchAccount: (accountId: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFirstRun, setIsFirstRun] = useState(false);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<PackageCapabilities | null>(null);
  const refreshGeneration = useRef(0);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const router = useRouter();
  const pathname = usePathname();

  const refreshUser = useCallback(async () => {
    const generation = ++refreshGeneration.current;
    try {
      const res = await fetch("/api/auth/me");
      if (res.ok) {
        const data = await res.json();
        if (generation !== refreshGeneration.current) return;
        setUser(data.user);
        setActiveAccountId(data.activeAccountId || null);
        setCapabilities(data.capabilities || null);
        setPermissions(Array.isArray(data.permissions) ? data.permissions.filter(isPermission) : []);
        setIsFirstRun(data.isFirstRun || false);

        // Handle navigation rules
        const isSuperAdmin = isPlatformAdminRole(data.user?.role);
        const defaultLanding = isSuperAdmin ? "/" : "/portal";
        const isAuthPage =
          pathname === "/login" ||
          pathname === "/setup" ||
          pathname === "/recovery" ||
          pathname.startsWith("/recovery/");

        if (data.isFirstRun && pathname !== "/setup" && pathname !== "/login") {
          router.push("/setup");
        } else if (!data.authenticated && !data.isFirstRun && !isAuthPage) {
          router.push(`/login?from=${encodeURIComponent(pathname)}`);
        } else if (data.authenticated && (pathname === "/login" || pathname === "/setup")) {
          router.push(defaultLanding);
        } else if (data.authenticated && !isSuperAdmin && !isAuthPage) {
          const destination = customerDestination(pathname);
          if (destination !== pathname) router.replace(destination + window.location.search);
        }
      } else if ((res.status === 401 || res.status === 403) && generation === refreshGeneration.current) {
        setUser(null);
        setActiveAccountId(null);
        setCapabilities(null);
        setPermissions([]);
        const isAuthPage =
          pathname === "/login" ||
          pathname === "/setup" ||
          pathname === "/recovery" ||
          pathname.startsWith("/recovery/");
        if (!isAuthPage) {
          router.push(`/login?from=${encodeURIComponent(pathname)}`);
        }
      }
    } catch {
      if (generation !== refreshGeneration.current) return;
      setUser(null);
      setCapabilities(null);
      setPermissions([]);
    } finally {
      if (generation === refreshGeneration.current) setLoading(false);
    }
  }, [pathname, router]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    const refresh = () => { void refreshUser(); };
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [refreshUser]);

  const login = async (
    username: string,
    password: string,
    options?: { totpCode?: string; recoveryCode?: string }
  ): Promise<{ success: boolean; requiresPasskey?: boolean; requires2FA?: boolean; message?: string; error?: string }> => {
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          password,
          totpCode: options?.totpCode,
          recoveryCode: options?.recoveryCode,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setUser(data.user);
        setIsFirstRun(false);
        return { success: true };
      }

      if (data.requiresPasskey) {
        return { success: false, requiresPasskey: true, message: data.message };
      }

      if (data.requires2FA) {
        return { success: false, requires2FA: true, message: data.message };
      }

      return { success: false, error: data.error || "Authentication failed" };
    } catch {
      return { success: false, error: "Network error occurred while signing in" };
    }
  };

  const loginWithPasskey = async (username?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      if (typeof window === "undefined" || !window.PublicKeyCredential) {
        return { success: false, error: "Passkeys are not supported on this browser or platform." };
      }

      const optRes = await fetch("/api/auth/webauthn/login-options", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });

      if (!optRes.ok) {
        const errData = await optRes.json();
        return { success: false, error: errData.error || "Failed to initiate passkey sign in." };
      }

      const options = await optRes.json();
      const assertionPayload = await performPasskeyAuthentication(options);

      const verifyRes = await fetch("/api/auth/webauthn/login-verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          credential: assertionPayload,
          challengeId: options.challengeId,
          username,
        }),
      });

      const verifyData = await verifyRes.json();
      if (verifyRes.ok && verifyData.success) {
        setUser(verifyData.user);
        setIsFirstRun(false);
        return { success: true };
      }

      return { success: false, error: verifyData.error || "Passkey verification failed." };
    } catch (err: any) {
      if (err.name === "NotAllowedError") {
        return { success: false, error: "Passkey prompt was cancelled or timed out." };
      }
      if (err.name === "InvalidStateError" || err?.message?.includes("already pending")) {
        return { success: false, error: "A passkey prompt was already open. Please try again." };
      }
      return { success: false, error: err.message || "Passkey authentication failed." };
    }
  };

  const setupAdmin = async (username: string, password: string, displayName?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch("/api/auth/setup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, displayName }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setUser(data.user);
        setIsFirstRun(false);
        return { success: true };
      }
      return { success: false, error: data.error || "Failed to setup administrator account" };
    } catch {
      return { success: false, error: "Network error occurred during setup" };
    }
  };

  const updateProfile = async (data: { displayName?: string; email?: string; timezone?: string }): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch("/api/auth/me", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const resData = await res.json();
      if (res.ok && resData.success) {
        setUser(resData.user);
        return { success: true };
      }
      return { success: false, error: resData.error || "Failed to update profile settings" };
    } catch {
      return { success: false, error: "Network error occurred while updating profile" };
    }
  };

  const switchAccount = async (accountId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch("/api/auth/switch-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accountId }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        ++refreshGeneration.current;
        setPermissions([]);
        setCapabilities(null);
        setActiveAccountId(data.activeAccountId);
        return { success: true };
      }
      return { success: false, error: data.error || "Failed to switch account" };
    } catch {
      return { success: false, error: "Network error while switching account" };
    }
  };

  const logout = async () => {
    ++refreshGeneration.current;
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch {
      // Ignore network errors on logout
    } finally {
      setUser(null);
      setActiveAccountId(null);
      setCapabilities(null);
      setPermissions([]);
      router.push("/login");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isFirstRun,
        activeAccountId,
        capabilities,
        permissions,
        login,
        loginWithPasskey,
        setupAdmin,
        updateProfile,
        switchAccount,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
